import {
	IconBook,
	IconBulb,
	IconChecklist,
	IconHelpCircle,
	IconMessage2,
	IconSparkles,
	IconTargetArrow,
} from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import { aiGenerationCreditCosts, type BlogPostContentType, blogPostContentTypes } from "@forento/core/models/ai"
import { type PlatformLanguageId, platformLanguageIds, platformLanguages } from "@forento/core/models/platform"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import AiGenerationDiscountNotice from "~/components/AiGenerationDiscountNotice"
import AiImagePicker from "~/components/AiImagePicker"
import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import InputField, { InputTextArea } from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import useFileUpload from "~/hooks/useFileUpload"
import { captureForentoAiEvent, getBuyAiCreditsUrl, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

const languageItems = platformLanguageIds.map(id => ({ id, title: platformLanguages[id].label }))

const AI_INPUT_LIMITS = { topic: 200, targetAudience: 300, contentToInclude: 1_000 } as const

const contentTypeConfig = {
	"educational-guide": {
		title: "Educational guide",
		description: "Explains a topic from the ground up",
		Icon: IconBook,
	},
	"how-to": {
		title: "How-to",
		description: "Walks the reader through the steps",
		Icon: IconChecklist,
	},
	"tips-list": {
		title: "Tips list",
		description: "A set of specific, standalone tips",
		Icon: IconBulb,
	},
	"case-study": {
		title: "Case study",
		description: "One situation from problem to outcome",
		Icon: IconTargetArrow,
	},
	opinion: {
		title: "Opinion",
		description: "Argues one position and backs it up",
		Icon: IconMessage2,
	},
	other: {
		title: "Other",
		description: "A custom article idea",
		Icon: IconHelpCircle,
	},
} satisfies Record<BlogPostContentType, { title: string; description: string; Icon: FC<{ className?: string }> }>

const contentTypeItems = blogPostContentTypes.map(id => ({ id, ...contentTypeConfig[id] }))

type Props = {
	isOpen: boolean
	defaultLanguage: PlatformLanguageId
	onClose(): void
	onGenerated(result: { postId: number; coverImageIdea: string | null; generationId: number }): Promise<void>
}
const AiBlogPostModal: FC<Props> = ({ isOpen, defaultLanguage, onClose, onGenerated }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const inputLimits = user.user?.isImpersonating ? undefined : AI_INPUT_LIMITS

	const [contentType, setContentType] = useState<BlogPostContentType>("educational-guide")
	const [topic, setTopic] = useState("")
	const [targetAudience, setTargetAudience] = useState("")
	const [contentToInclude, setContentToInclude] = useState("")
	const [coverImage, setCoverImage] = useState<File | null>(null)
	const [language, setLanguage] = useState<PlatformLanguageId>(defaultLanguage)

	const imageUpload = useFileUpload()
	const generate = query.ai.generateBlogPost.useMutation({
		async onSuccess(result) {
			captureForentoAiEvent("forento_ai_generation_succeeded", { type: "blog-post" })
			await onGenerated(result)
			await user.reload()
			reset()
			onClose()
		},
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "blog-post" })
			await alert.show("Error", "Failed to generate blog post. Please try again.")
		},
	})

	useEffect(() => {
		if (isOpen) setLanguage(defaultLanguage)
	}, [defaultLanguage, isOpen])

	const generationCost = aiGenerationCreditCosts["blog-post"]
	const hasEnoughCredits = platform.aiCreditBalance >= generationCost
	useForentoAiModalTracking({
		type: "blog-post",
		isFormVisible: isOpen,
		creditBalance: platform.aiCreditBalance,
		cost: generationCost,
	})
	const isGenerating = imageUpload.isPending || generate.isPending
	const canGenerate = topic.trim().length > 0 && targetAudience.trim().length > 0 && hasEnoughCredits && !isGenerating

	const handleGenerate = async () => {
		if (!canGenerate) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "blog-post" })

		let coverImageUrl: string | null = null
		try {
			if (coverImage !== null) {
				const uploaded = await imageUpload.upload({ file: coverImage, kind: "landing-image" })
				if (uploaded === null) return
				coverImageUrl = uploaded.publicUrl
			}
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to upload the cover image. Please try again.")
			return
		}

		generate.mutate({ topic, targetAudience, contentType, contentToInclude, coverImageUrl, language })
	}

	const reset = () => {
		setContentType("educational-guide")
		setTopic("")
		setTargetAudience("")
		setContentToInclude("")
		setCoverImage(null)
		setLanguage(defaultLanguage)
	}

	return (
		<DefaultModal
			width={760}
			isOpen={isOpen}
			onSubmit={handleGenerate}
			onClose={
				isGenerating
					? undefined
					: () => {
							reset()
							onClose()
						}
			}
		>
			<ModalTitle>Write a blog post with Forento AI</ModalTitle>
			<ModalText>
				You get an editable draft with the SEO fields filled in, so nothing is published until you say so.
			</ModalText>
			<ModalInputs>
				<div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-3">
					<div>
						<div className="text-xs text-gray-500">Available balance</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(platform.aiCreditBalance)} credits
						</div>
					</div>
					<div>
						<div className="text-xs text-gray-500">This generation costs</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(generationCost)} credits
						</div>
					</div>
					<div className="flex items-center sm:justify-end">
						<Button
							variant="secondary"
							onClick={getBuyAiCreditsUrl("blog-post")}
							className="w-full sm:w-auto"
						>
							Purchase credits
						</Button>
					</div>
					<AiGenerationDiscountNotice className="sm:col-span-3" />
					{!hasEnoughCredits && (
						<p className="text-sm text-red-600 sm:col-span-3">
							Purchase more credits before generating a blog post.
						</p>
					)}
				</div>
				<div className="grid gap-2 sm:grid-cols-2">
					{contentTypeItems.map(item => {
						const Icon = item.Icon
						const isSelected = item.id === contentType
						return (
							<button
								key={item.id}
								type="button"
								className={cn(
									"flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors",
									isSelected
										? "border-primary bg-primary/5 ring-primary/20 text-gray-950 ring-1"
										: "hover:border-primary/40 border-black/10 bg-white text-gray-700 hover:bg-gray-50",
								)}
								onClick={() => setContentType(item.id)}
							>
								<div
									className={cn(
										"flex size-10 shrink-0 items-center justify-center rounded-lg",
										isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-500",
									)}
								>
									<Icon className="size-5" />
								</div>
								<div className="min-w-0">
									<div className="text-sm font-semibold">{item.title}</div>
									<div className="text-xs leading-relaxed text-gray-500">{item.description}</div>
								</div>
							</button>
						)
					})}
				</div>

				<InputField
					label="Topic or keyword focus"
					value={topic}
					onChange={setTopic}
					placeholder="How to price an online course"
					maxCharacters={inputLimits?.topic}
				/>
				<InputField
					label="Target audience"
					value={targetAudience}
					onChange={setTargetAudience}
					placeholder="First-time course creators who have an audience but have never sold anything"
					maxCharacters={inputLimits?.targetAudience}
				/>
				<InputTextArea
					label="Anything to include"
					value={contentToInclude}
					onChange={setContentToInclude}
					placeholder="Points to cover, your own experience, examples, products to mention, or questions to answer"
					maxCharacters={inputLimits?.contentToInclude}
				/>
				<AiImagePicker
					label="Cover image"
					description="Optional. Without one you get a suggestion for what to use."
					files={coverImage ? [coverImage] : []}
					onChange={files => setCoverImage(files[0] ?? null)}
				/>
				<Dropdown
					label="Language"
					items={languageItems}
					selectedItemId={language}
					onChange={id => setLanguage(id as PlatformLanguageId)}
				/>
			</ModalInputs>
			<ModalButtons>
				<Button
					variant="secondary"
					onClick={() => {
						reset()
						onClose()
					}}
					isDisabled={isGenerating}
				>
					Cancel
				</Button>
				<SubmitButton variant="primary" isDisabled={!canGenerate} isLoading={isGenerating}>
					<IconSparkles className="size-4" />
					Write post ({toNumberFormat(generationCost)} credits)
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

export default AiBlogPostModal
